import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { ArrowDown, Zap, Home, TrendingUp } from 'lucide-react';
import AdminLogin from '../admin/AdminLogin';
import ModalContactForm from '../forms/ModalContactForm';
import { sendButtonClickEvent } from '../../utils/postMessageBridge';

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [modalState, setModalState] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [tapCount, setTapCount] = useState(0);
  
  useEffect(() => {
    setIsVisible(true);
  }, []);

  const openFormModal = () => {
    sendButtonClickEvent('HERO_BERATUNGSTERMIN', {
      section: 'hero',
      action: 'open-modal'
    });
    
    // Mobile: Sofort öffnen, kein Scroll nötig (Vollbild-Modal)
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      setFormModalOpen(true);
    } else {
      // Desktop: Scroll nach oben, dann Modal öffnen
      try {
        window.parent.postMessage({ type: 'SCROLL_TO_TOP' }, '*');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        console.log('PostMessage failed:', e);
      }
      
      setTimeout(() => {
        setFormModalOpen(true);
      }, 400);
    }
  };

  const handleIconClick = () => {
    setTapCount(prev => prev + 1);
    setTimeout(() => setTapCount(0), 3000);
    if (tapCount === 2) {
      setModalState(true);
      setTapCount(0);
    }
  };

  return (
    <section className="relative pb-20 md:pb-32 flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50/30 overflow-hidden">
      {/* SECTION NUMBERING - FULLY TRANSPARENT */}
      <div className="absolute top-4 left-4 z-0 bg-transparent text-transparent px-3 py-2 rounded-lg font-bold text-sm sm:text-base shadow-none opacity-0 hidden">
        SEKTION 1: HERO
      </div>

      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-72 h-72 rounded-full mix-blend-multiply filter blur-xl animate-pulse"
             style={{backgroundColor: '#91d33d'}}></div>
        <div className="absolute top-40 right-20 w-72 h-72 rounded-full mix-blend-multiply filter blur-xl animate-pulse" 
             style={{backgroundColor: '#57c558', animationDelay: '2s'}}></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 rounded-full mix-blend-multiply filter blur-xl animate-pulse" 
             style={{backgroundColor: '#49ab49', animationDelay: '4s'}}></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div className={`space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="space-y-6">
                <h1 
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight relative z-50"
                  style={{ lineHeight: '1.3', overflow: 'visible' }}
                >
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-green-dark to-brand-green relative z-50">
                    Wärmepumpen in Berlin & Brandenburg – bis zu 70 % Förderung
                  </span>
                  <span className="block text-3xl sm:text-4xl lg:text-5xl mt-4">
                    Nie wieder Öl und Gas
                  </span>
                  <span className="block text-2xl sm:text-3xl lg:text-4xl mt-2 text-gray-700">
                    Willkommen in der Zukunft des Heizens
                  </span>
                </h1>
                
                <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
                  Nutzen Sie staatliche Förderung und senken Sie Ihre Energiekosten dauerhaft – 
                  mit einer Wärmepumpe vom Qualitätsführer aus Berlin & Brandenburg.
                </p>
              </div>

              {/* Key Features */}
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 shadow-sm">
                  <div 
                    onClick={handleIconClick}
                    className="w-10 h-10 bg-gradient-to-r from-brand-green-dark to-brand-green rounded-lg flex items-center justify-center"
                  >
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-semibold text-gray-800">Bis 70% Förderung</span>
                </div>
                
                <div className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 shadow-sm">
                  <div className="w-10 h-10 bg-gradient-to-r from-brand-green-light to-brand-green rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-semibold text-gray-800">80% weniger Kosten</span>
                </div>
                
                <div className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm rounded-xl px-4 py-3 shadow-sm">
                  <div className="w-10 h-10 bg-gradient-to-r from-brand-green to-brand-green-hover rounded-lg flex items-center justify-center">
                    <Home className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-semibold text-gray-800">Immobilienwert +15%</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  onClick={openFormModal}
                  size="lg"
                  className="bg-brand-orange hover:bg-brand-orange-hover text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                >
                  Beratungstermin
                </Button>
              </div>

              {/* Trust Elements */}
              <div className="flex items-center space-x-8 pt-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">2.000+</div>
                  <div className="text-sm text-gray-600">Projekte</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">13+</div>
                  <div className="text-sm text-gray-600">Jahre Erfahrung</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">100%</div>
                  <div className="text-sm text-gray-600">Berlin & Brandenburg</div>
                </div>
              </div>
            </div>

            {/* Visual */}
            <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="relative">

                {/* Main Image - Your Heat Pump Image */}
                <div className="w-full h-96 lg:h-[500px] rounded-2xl shadow-2xl overflow-hidden relative bg-white flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_solar-leads/artifacts/n8licu5l_Waermepumpe.jpg"
                    alt="Wärmepumpe - moderne Heizlösung"
                    title="Moderne Luft-Wasser-Wärmepumpe für effiziente Heizung in Berlin und Brandenburg"
                    width="800"
                    height="600"
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Floating Elements */}
                  <div className="absolute top-4 right-4 bg-white rounded-lg p-3 shadow-lg animate-bounce">
                    <div className="text-sm font-semibold text-brand-green">70% Förderung</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ArrowDown className="w-6 h-6 text-gray-400" />
        </div>
      </div>

      
      {modalState && (
        <AdminLogin 
          isOpen={modalState} 
          onClose={() => setModalState(false)} 
        />
      )}
      
      {/* Modal Contact Form */}
      <ModalContactForm 
        isOpen={formModalOpen} 
        onClose={() => setFormModalOpen(false)} 
      />
    </section>
  );
};

export default HeroSection;